// frontend/src/pages/ConfirmEmail.jsx
//
// Where the confirmation link from an email change lands
// (authentication/services/email_change.py mails
// `/confirm-email/<uid>/<token>`). Confirming swaps the address onto the
// account and signs the user's other sessions out.
//
// The token is spent by an explicit click, NOT on mount. A mail client or
// scanner that follows links would otherwise consume it before the merchant
// ever opened the page, and they would then see nothing but "invalid link"
// with the change silently already applied. ResetPassword.jsx can verify on
// mount because its mount call only CHECKS the token; there is no separate
// check endpoint here, and adding one to preserve the shape would mean two
// round trips to spend one link.

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sessionService, sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { ICON, CheckCircle } from '../icons';
import '../components/login/Login.css';

const STATE = {
  READY: 'ready',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
};

export default function ConfirmEmail() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState(STATE.READY);
  const [error, setError] = useState('');
  const [theme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const handleConfirm = async () => {
    setError('');
    setState(STATE.SUBMITTING);

    try {
      await sessionService.initCSRF();

      const response = await sessionApi.post('/auth/user-profile/confirm-email/', {
        uid,
        token,
      });
      if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/user-profile/confirm-email/');
      setState(STATE.SUCCESS);
    } catch (err) {
      // Every refusal here is typed (invalid_email_change_link,
      // pending_email_taken, rate_limit_exceeded), so the merchant gets the
      // reason rather than one catch-all sentence. The server's own message
      // is dev-facing and never rendered.
      console.error('Confirm email failed:', err.message);
      setError(translateRequestError(err, t, 'confirmEmail.errors.failed'));
      setState(STATE.READY);
    }
  };

  const logoSrc = '/images/manexys-logo.png';

  return (
    <div className="login-container">

      {/* Left: brand panel */}
      <div className="login-brand">
        <div className="login-brand-content">
          <img src={logoSrc} alt="Manexys" className="login-logo" />
          <div className="login-brand-accent" />
          <p className="login-tagline">Forecast with Precision.<br />Execute with Confidence.</p>
        </div>
        <div className="login-brand-footer">
          <span>manexys.com</span>
        </div>
      </div>

      {/* Right: action panel */}
      <div className="login-panel">
        <div className="login-form-wrap">

          {state === STATE.SUCCESS ? (
            <div className="setup-success">
              <div className="setup-success-icon">
                <CheckCircle size={ICON.display} />
              </div>
              <h1 className="login-title" style={{ textAlign: 'center' }}>
                {t('confirmEmail.success.title')}
              </h1>
              <p className="login-subtitle" style={{ textAlign: 'center' }}>
                {t('confirmEmail.success.message')}
              </p>
              <button
                className="login-button"
                onClick={() => navigate('/login')}
              >
                {t('confirmEmail.success.signIn')}
              </button>
            </div>
          ) : (
            <>
              <h1 className="login-title">{t('confirmEmail.title')}</h1>
              <p className="login-subtitle">{t('confirmEmail.subtitle')}</p>

              {error && (
                <div className="login-info login-info-error">{error}</div>
              )}

              <button
                className="login-button"
                onClick={handleConfirm}
                aria-busy={state === STATE.SUBMITTING}
                disabled={state === STATE.SUBMITTING}
              >
                {t('confirmEmail.confirm')}
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
